import React from 'react';
import SkillActions from 'actions/SkillActions';
import UserActions from 'actions/UserActions';
import connectToStores from 'alt-utils/lib/connectToStores';
import AuthStore from 'stores/AuthStore';
import SkillStore from 'stores/SkillStore';
import UserStore from 'stores/UserStore';
import {List} from 'immutable';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import {AdhocForm} from 'components/coaching/AdhocForm';
import CoachingPlan from 'records/CoachingPlan';
import moment from 'moment';
import {browserHistory} from 'react-router';
import confirm from 'utils/confirm';
import {unsavedMessage} from 'utils/const';
import noop from 'lodash/noop';
import CoachingContentDetail, {
  defaultSkillGoal,
  nonSkillRelated,
} from 'records/CoachingContentDetail';
import ToastStore from '@store/toast/ToastStore';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import CoachingReviewFileUpload from 'records/CoachingReviewFileUpload';
import CoachingContentDetailScore from 'records/CoachingContentDetailScore';
import Loading from 'components/pure/utils/Loading';
import Button from '@material-ui/core/Button';
import {ensureReviewMetadata} from 'components/StatsPage2.utils';

@connectToStores
export default class AdHocCoachingDialogContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onChange = this.onChange.bind(this);
    this.loadData = this.loadData.bind(this);
    this.toggleSkillType = this.toggleSkillType.bind(this);

    this.state = {
      isSubmitting: false,
      reviewType: undefined,
      contentDetail: nonSkillRelated,
      showCustom: false,
      ready: false,
    };
  }

  static getStores() {
    return [AuthStore, SkillStore, UserStore, DirectReportsStore, CoachingReviewTypeStore];
  }

  static getPropsFromStores({rep = {}}) {
    const teamId = rep.teamId;

    const [reviewTypesLoaded, reviewTypes] = typesAndCategories(CoachingReviewTypeStore);

    return {
      users: UserStore.getState().users,
      coach: AuthStore.getState().user,
      skills: SkillStore.getState().skillsByTeam.get(teamId, List()),
      reviewTypesLoaded,
      reviewTypes,
    };
  }

  componentDidMount() {
    const {rep} = this.props;
    if (rep) {
      this.loadData(rep)
        .then(() => {
          // Sets default if types are loaded, otherwise will get picked up in update.
          this.setState({
            reviewType: this.defaultReviewType(),
          });
        })
        .catch(console.error);
    } else {
      // Ditto above re: reviewType.
      this.setState({ready: true, reviewType: this.defaultReviewType()});
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // If review types weren't loaded on mount, but have now loaded, set the default.
    if (!this.state.reviewType && !prevProps.reviewTypesLoaded && this.props.reviewTypesLoaded) {
      this.setState({
        reviewType: this.defaultReviewType(),
      });
    }
  }

  async loadData(rep) {
    ensureReviewMetadata();
    UserActions.fetchAll.defer();
    const [[skills], skillBreakdown] = await Promise.all([
      SkillActions.fetchAllByTeam({team_id: rep.teamId}),
      DirectReportsActions.getSkillBreakdown(List([rep.id])),
    ]);

    this.setState({ready: true});

    const challengedBySkill = skillBreakdown?.find(c => c.userId === rep.id)?.worstSkill?.skillName;

    const defaultSkill = challengedBySkill && skills.find(s => challengedBySkill === s.skill);

    if (defaultSkill) {
      this.setState({
        contentDetail: new CoachingContentDetail({
          skillId: defaultSkill.id,
          skillName: defaultSkill.skill,
          skillGoal: defaultSkillGoal,
        }),
      });
    }
  }

  defaultPlan() {
    const now = moment();
    const {rep, coach} = this.props;
    const {reviewType} = this.state;
    const skillRelated = this.state.contentDetail.isSkillBased;
    const detail = skillRelated ? this.state.contentDetail : nonSkillRelated;
    const skillName = detail.skillName || detail.custom;

    return new CoachingPlan({
      adHoc: true,
      contentDetails: [detail],
      schedule: {
        weeksBetween: 1,
        daysOfWeek: [now.format('dddd')],
        time: {hour: now.hours(), minute: now.minutes()},
        recurrenceCount: 1,
        durationInMinutes: 15,
      },
      coach: {
        id: coach.id,
        info: coach.toJS(),
        prepNumberOfCalls: 0,
        prepShouldComment: false,
      },
      recipient: {
        id: rep.id,
        info: rep.toJS(),
        prepNumberOfCalls: 0,
        prepShouldComment: false,
      },
      name: `${skillRelated ? skillName : reviewType.name} - Ad Hoc, ${now.format('MMM DD YYYY')}`,
      objective: skillRelated
        ? `${coach.getFullName()} should provide feedback on ${skillName} to ${rep.getFullName()}`
        : `${coach.getFullName()} should evaluate ${rep.getFullName()} according to ${
            reviewType.name
          }`,
      reviewTypeIds: [reviewType.id],
    });
  }

  async onSubmit() {
    const {dashV2Url, rep, coach, callId} = this.props;
    const reviewType = this.state.reviewType;
    const plan = this.defaultPlan();
    this.setState({isSubmitting: true});
    try {
      const {id: planId} = await CoachingPlanActions.create(plan);
      const retrievedPlan = await CoachingPlanActions.retrieve(planId, {
        includeReviews: true,
      });
      const review = retrievedPlan.getNextReview();

      if (callId && review) {
        await CoachingReviewActions.updateReviewFileUploads(
          review.id,
          List([
            new CoachingReviewFileUpload({
              addedByUserId: List([coach.id]),
              fileuploadId: callId,
              reviewFeedbackList: retrievedPlan.contentDetails.map(
                contentDetail => new CoachingContentDetailScore({contentDetail})
              ),
            }),
          ])
        );
      }

      if (reviewType) {
        await CoachingReviewActions.updateReview(review.set('reviewTypeId', reviewType.id));
        // Make sure the updated review is available in the stores
        await CoachingPlanActions.retrieve(planId, {includeReviews: true});
      }

      browserHistory.push({
        pathname: dashV2Url,
        query: {
          planId: planId,
          repId: rep.id,
          reviewId: review.id,
          autoStartReview: true,
        },
      });
      this.props.onClose();
    } catch (e) {
      console.error(e);
      ToastStore.addToast({
        type: 'error',
        message: 'There was a problem creating the review session.',
      });
      this.setState({isSubmitting: false});
    }
  }

  onChange([skill, id]) {
    let contentDetail;
    if (!(skill || id)) {
      contentDetail = new CoachingContentDetail({custom: ''});
    } else {
      const detail = id
        ? {skillName: skill, skillId: id, skillGoal: defaultSkillGoal}
        : {custom: skill};
      contentDetail = new CoachingContentDetail(detail);
    }
    this.setState({contentDetail});
  }

  toggleSkillType() {
    this.setState({showCustom: !this.state.showCustom});
  }

  onCancel() {
    confirm(unsavedMessage).then(this.props.onClose, noop);
  }

  isSubmitValid() {
    const {rep} = this.props;
    const {reviewType} = this.state;
    return this.isSkillValid() && !!reviewType && rep;
  }

  isSkillValid() {
    const {contentDetail, showCustom} = this.state;
    if (contentDetail === nonSkillRelated) {
      // No skill selected. Always valid.
      return true;
    }
    return showCustom ? !!contentDetail.custom : !!contentDetail.skillName;
  }

  defaultReviewType() {
    const {reviewTypes, noCallOnly} = this.props;
    return reviewTypes.find(rt => rt.isDefault && (noCallOnly ? !rt.includesCalls : true));
  }

  render() {
    if (!(this.state.ready && this.props.reviewTypesLoaded)) {
      return <Loading />;
    }

    const {onChange, reviewTypes} = this.props;
    const {reviewType} = this.state;

    return (
      <div>
        <AdhocForm
          {...this.props}
          {...this.state}
          reviewType={reviewType}
          setRep={rep => {
            this.props.setRep(rep);
            this.loadData(rep);
          }}
          onChange={this.onChange}
          toggleSkillType={this.toggleSkillType}
          onChangeReviewType={({id}) => {
            const reviewType = reviewTypes.get(id);
            this.setState({reviewType});
            onChange && onChange();
          }}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="create-button meeting ev-button"
          disabled={!this.isSubmitValid()}
          onClick={() => (this.isSubmitValid() ? this.onSubmit() : null)}>
          Begin
        </Button>
      </div>
    );
  }
}
